/**
 * The settings shell — Step 93 (was: a horizontal tab strip).
 *
 * History, because it explains the shape: this was originally a second left
 * rail beside the main Sidebar, which Kai rejected — "so that there's not a
 * load of side bars". It became a horizontal tab strip. That held nine tabs,
 * was already wrapping, and had nowhere to say what each page was FOR.
 *
 * It is now the reference tool's shape: `/admin` is a grouped, searchable
 * card index (`AdminHomePage`), and each settings page is a destination you
 * navigate INTO with a way back. Still one sidebar total.
 *
 * Why the strip had to go rather than grow: settings is somewhere you visit
 * rarely and need to SEARCH. A strip cannot carry a description, and
 * "Mail" vs "Inbound mailboxes" is unresolvable without one. It also could
 * not show what is planned-but-not-built without looking broken, which the
 * index does deliberately.
 *
 * The header here renders only on a CHILD route — the index draws its own —
 * so there is never a doubled title.
 */
import { Link, Outlet, useLocation } from 'react-router-dom';
import { ChevronLeft, Settings2, MonitorSmartphone } from 'lucide-react';
import { cardForPath } from './adminSections';

export function AdminLayout() {
  const { pathname } = useLocation();
  const isIndex = pathname === '/admin' || pathname === '/admin/';
  const card = cardForPath(pathname);

  return (
    <div className="flex h-full min-h-0 flex-col">
      {!isIndex && (
        <div className="shrink-0 border-b px-6 py-3">
          <div className="mx-auto flex max-w-6xl items-center gap-3">
            <Link
              to="/admin"
              className="flex items-center gap-1 rounded-md px-1.5 py-1 text-xs text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              <Settings2 className="h-3.5 w-3.5" />
              Settings
            </Link>
            {/* Named from the SAME registry the index renders, so a page
                cannot end up titled one thing on the card and another here. */}
            {card && (
              <>
                <span className="text-xs text-muted-foreground/50">/</span>
                <span className="text-xs font-medium">{card.label}</span>
              </>
            )}
          </div>
        </div>
      )}
      {/* Step 141 — settings are DESKTOP-ONLY ON PURPOSE, and say so rather
          than quietly collapsing. Kai's ruling: the surfaces that need mobile
          get it properly; the ones that do not state it. Nothing here is
          blocked — the builder, the matrix and the catalogue editor simply
          have more controls than a phone has room for. */}
      <p className="flex items-start gap-2 border-b bg-amber-500/10 px-4 py-2 text-xs text-amber-800 dark:text-amber-300 md:hidden">
        <MonitorSmartphone className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        Settings are built for a desktop screen. Everything works here, but the editors are easier with room.
      </p>
      <main className="min-h-0 flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
}
